import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrgRequest } from './api/organizations/orgSlice';
import { fetchSubscriptionRequest } from './api/subscription/subscriptionSlice';
import { loadingPrerequisites } from './api/app/appStateSelector';
import { selectRequiresOrgSelection, selectSessionReady, sessionResolved } from './api/auth/authSlice';
import { authApiClient } from './utils/axios';
import { DashboardLayout, DashboardSidebarPageItem, PageContainer } from '@toolpad/core';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import UserAccount from './components/UserAccount';
import DescriptionIcon from '@mui/icons-material/Description';
import AuthenticatedRoutes from './routes/AuthenticatedRoutes';
import { BusinessCenterOutlined, InsightsOutlined, Inventory } from '@mui/icons-material';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import BottomBar from './components/BottomBar/BottomBar';
import ScreenLoader from './components/ScreenLoader';
import GlobalSnackbar from './components/GlobalSnackbar';
import SignupPage from './resources/users/pages/SignupPage';
import ForgotPasswordPage from './resources/forgotPassword/pages/ForgotPasswordPage';
import LoginPage from './resources/login/pages/LoginPage';
import SelectOrganizationPage from './resources/login/pages/SelectOrganizationPage';
import ArticleIcon from '@mui/icons-material/Article';
import { loadCodeListsRequest } from './api/codelist/codeListsSlice';
import { ReactRouterAppProvider } from '@toolpad/core/react-router';
import OnboardingPage from './resources/onboarding/pages/OnboardingPage';
import SideBottomBar from './components/SideBottomBar';
import { fetchUserRequest } from './api/user/userSlice';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { Box, Stack } from '@mui/material';
import LanguageSelect from './components/LanguageSelect';
import { useTranslatedMessages } from './hooks/useTranslatedMessages';
import createAppTheme from './theme';
import { ltrCache, rtlCache } from './helper/rtlCache';
import { CacheProvider } from '@emotion/react';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import appConfig from './config/appConfig';
import EnvironmentBanner from './components/EnvironmentBanner';
import { resolveSafePostAuthPath } from './utils/authSession';

const getNavigation = (n, { showReconciliation } = {}) => [
  {
    segment: 'dashboard',
    title: n.mis_dashboard,
    icon: <InsightsOutlined />,
  },
  {
    kind: 'divider',
  },
  {
    segment: 'business-profiles',
    title: n.business_profiles,
    pattern: 'business-profiles{/:id}*',
    icon: <BusinessCenterOutlined />,
  },
  {
    segment: 'products',
    title: n.products_services,
    pattern: 'products',
    icon: <Inventory />,
  },

  {
    title: n.sales,
    icon: <ShoppingCartIcon />,
    children: [
      {
        segment: 'customers',
        title: n.customers,
        pattern: 'customers{/:id}*',
        icon: <PeopleOutlineOutlinedIcon />,
      },
      {
        segment: 'sales/documents',
        title: n.documents,
        icon: <DescriptionIcon />,
        pattern: 'sales/documents{/:id}*',
      },
      {
        segment: 'sales/proforma-invoices',
        title: n.proforma_invoices,
        icon: <FileCopyIcon />,
        pattern: 'sales/proforma-invoices{/:id}*',
      },
    ],
  },
  {
    title: n.purchases,
    icon: <ReceiptLongIcon />,
    children: [
      {
        segment: 'suppliers',
        title: n.suppliers,
        icon: <PeopleOutlineOutlinedIcon />,
      },
      {
        segment: 'purchases/documents',
        title: n.documents,
        icon: <DescriptionIcon />,
        pattern: 'purchases/documents{/:id}*',
      },
    ],
  },
  {
    title: n.register,
    icon: <ArticleIcon />,
    children: [
      {
        segment: 'sales-invoice-register',
        title: n.sales_invoice_register,
        icon: <ShoppingCartIcon />,
        pattern: 'sales-invoice-register{/:id}*',
      },
      {
        segment: 'purchase-invoice-register',
        title: n.purchase_invoice_register,
        icon: <ReceiptLongIcon />,
        pattern: 'purchase-invoice-register{/:id}*',
      },
    ],
  },
  {
    segment: 'bulk-upload',
    title: n.bulk_upload,
    icon: <CloudUploadOutlinedIcon />,
  },

  {
    kind: 'divider',
  },
  {
    segment: 'reporting-reconciliation',
    title: n.reporting_reconciliation,
    icon: <ArticleIcon />,
    children: [
      {
        segment: 'vat-returns',
        title: n.vat_returns,
      },
      {
        segment: 'document-report',
        title: n.document_reports,
      },
      ...(showReconciliation
        ? [
            {
              segment: 'reconciliation',
              title: n.reconciliation,
            },
          ]
        : []),
    ],
  },
  {
    kind: 'divider',
  },
];

const NonHeaderLayout = () => (
  <Box
    sx={{
      position: 'relative',
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Box sx={{ position: 'fixed', top: 16, insetInlineEnd: 16, zIndex: 1200 }}>
      <LanguageSelect sx={{ minWidth: 120 }} />
    </Box>
    <Outlet />
  </Box>
);
// Component to handle redirect to login with original location
const RedirectToLogin = () => {
  const location = useLocation();
  const redirectTo = resolveSafePostAuthPath(location.pathname + location.search);

  return <Navigate to="/login" state={{ from: redirectTo, redirectTo }} replace />;
};

const RedirectToSelectOrganization = () => {
  const location = useLocation();

  return (
    <Navigate
      to="/select-organization"
      state={{ redirectTo: resolveSafePostAuthPath(location.pathname + location.search) }}
      replace
    />
  );
};

const ToolbarAccount = () => {
  return <UserAccount />;
};

function App() {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const sessionChecked = useSelector((state) => state.auth.sessionChecked);
  const sessionReady = useSelector(selectSessionReady);
  const requiresOrgSelection = useSelector(selectRequiresOrgSelection);
  const loading = useSelector(loadingPrerequisites);
  const dispatch = useDispatch();
  const orgLoading = useSelector((state) => state.org.loading);
  const subscriptionLoading = useSelector((state) => state.subscription.loading);
  const showReconciliation = Boolean(
    useSelector((state) => state.subscription.subscription?.features?.enable_document_reconciliation),
  );

  const [expandedModule, setExpandedModule] = useState(null);

  const { language, direction, isRTL, navbar: n } = useTranslatedMessages();
  const theme = useMemo(() => createAppTheme(direction, language), [direction, language]);
  const navigation = useMemo(() => getNavigation(n, { showReconciliation }), [n, showReconciliation]);

  useEffect(() => {
    dispatch(loadCodeListsRequest());
  }, [dispatch]);

  useEffect(() => {
    authApiClient
      .get('/me', { skipSessionRedirect: true })
      .then(() => {
        dispatch(sessionResolved({ authenticated: true }));
      })
      .catch(() => {
        dispatch(sessionResolved({ authenticated: false }));
      });
  }, [dispatch]);

  useEffect(() => {
    if (sessionReady) {
      dispatch(fetchOrgRequest());
      dispatch(fetchSubscriptionRequest());
      dispatch(fetchUserRequest());
    }
  }, [dispatch, sessionReady]);

  if (!sessionChecked || loading || (sessionReady && (orgLoading || subscriptionLoading))) {
    return <ScreenLoader />;
  }

  return (
    <CacheProvider key={direction} value={isRTL ? rtlCache : ltrCache}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100dvh',
          maxHeight: '100dvh',
          overflow: 'hidden',
        }}
      >
        <EnvironmentBanner />
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <ReactRouterAppProvider key={direction} theme={theme} navigation={navigation}>
            <Routes>
              <Route path="/onboard" element={<OnboardingPage />} />
              {sessionReady ? (
                <Route
                  path="/*"
                  element={
                    <DashboardLayout
                      sx={{ flex: 1, minHeight: 0, width: '100%', overflow: 'hidden' }}
                      key={direction}
                      renderPageItem={(item) => {
                        if (!item.children) {
                          return <DashboardSidebarPageItem item={item} />;
                        }

                        const isExpanded = expandedModule === item.title;

                        return (
                          <Box
                            onClick={(event) => {
                              if (event.target.closest('a')) {
                                return;
                              }

                              setExpandedModule(isExpanded ? null : item.title);
                            }}
                          >
                            <DashboardSidebarPageItem item={item} expanded={isExpanded} />
                          </Box>
                        );
                      }}
                      slots={{
                        toolbarAccount: ToolbarAccount,
                        sidebarFooter: SideBottomBar,
                      }}
                      branding={{
                        title: '',
                        homeUrl: '/dashboard',
                        logo: <img src={appConfig.brandLogoPath} alt={`${appConfig.brandName} logo`} />,
                      }}
                    >
                      <PageContainer maxWidth={'xxl'} breadcrumbs={[]} title="">
                        <Routes>
                          <Route path="/" element={<Navigate to="/dashboard" replace />} />
                          {AuthenticatedRoutes()}
                        </Routes>
                      </PageContainer>
                      <BottomBar />
                    </DashboardLayout>
                  }
                />
              ) : isAuthenticated && requiresOrgSelection ? (
                <Route element={<NonHeaderLayout />}>
                  <Route path="/select-organization" element={<SelectOrganizationPage />} />
                  <Route path="*" element={<RedirectToSelectOrganization />} />
                </Route>
              ) : (
                <Route element={<NonHeaderLayout />}>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup/:referralCode" element={<SignupPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="*" element={<RedirectToLogin />} />
                </Route>
              )}
            </Routes>

            <ScreenLoader />
            <GlobalSnackbar />
          </ReactRouterAppProvider>
        </Box>
      </Box>
    </CacheProvider>
  );
}

export default App;
